import { useState, useEffect, useRef, Fragment } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  Polygon,
  useMap,
  Pane,
  Tooltip,
} from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Info } from "@mui/icons-material";

const LoadingScreen = ({ visible, t }) => {
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const steps = [
      { target: 30, delay: 200 },
      { target: 55, delay: 500 },
      { target: 75, delay: 900 },
      { target: 90, delay: 1400 },
    ];

    let timeouts = [];
    steps.forEach(({ target, delay }) => {
      timeouts.push(
        setTimeout(() => {
          setProgress(target);
        }, delay)
      );
    });

    return () => timeouts.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!visible) {
      setProgress(100);
    }
  }, [visible]);

  if (!visible && phase === 2) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#040810",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "all" : "none",
        transition: "opacity 0.8s cubic-bezier(0.4,0,0.2,1)",
      }}
      onTransitionEnd={() => {
        if (!visible) setPhase(2);
      }}
    >
      <style>{`
        @keyframes scanLine {
          0%   { transform: translateY(-100%); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(2200%); opacity: 0; }
        }
        @keyframes radarSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes radarPulse {
          0%   { transform: scale(1); opacity: 0.6; }
          50%  { transform: scale(1.08); opacity: 0.3; }
          100% { transform: scale(1); opacity: 0.6; }
        }
        @keyframes dotBlink {
          0%, 100% { opacity: 0.2; transform: scale(0.7); }
          50%       { opacity: 1;   transform: scale(1);   }
        }
        @keyframes skeletonShimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes gridPulse {
          0%, 100% { opacity: 0.03; }
          50%       { opacity: 0.07; }
        }
        .shimmer-box {
          background: linear-gradient(
            90deg,
            rgba(255,255,255,0.04) 0%,
            rgba(255,255,255,0.09) 40%,
            rgba(255,255,255,0.04) 80%
          );
          background-size: 400px 100%;
          animation: skeletonShimmer 1.6s ease-in-out infinite;
        }
      `}</style>

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(34,197,94,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34,197,94,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          animation: "gridPulse 3s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(34,197,94,0.6) 20%, rgba(34,197,94,0.9) 50%, rgba(34,197,94,0.6) 80%, transparent 100%)",
            boxShadow: "0 0 20px rgba(34,197,94,0.4), 0 0 60px rgba(34,197,94,0.15)",
            animation: "scanLine 3.5s cubic-bezier(0.4,0,0.6,1) infinite",
          }}
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          animation: "fadeUp 0.6s ease both",
        }}
      >
        <Box sx={{ position: "relative", width: 120, height: 120 }}>
          {[1, 0.65, 0.38].map((scale, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                border: "1px solid",
                borderColor: `rgba(34,197,94,${0.12 + i * 0.06})`,
                transform: `scale(${scale})`,
                transformOrigin: "center",
                animation: `radarPulse ${2 + i * 0.4}s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}

          <Box
            sx={{
              position: "absolute",
              inset: 8,
              borderRadius: "50%",
              overflow: "hidden",
              animation: "radarSpin 2.4s linear infinite",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "conic-gradient(from 0deg, transparent 270deg, rgba(34,197,94,0.5) 360deg)",
              }}
            />
          </Box>

          <Box
            sx={{
              position: "absolute",
              inset: "50%",
              transform: "translate(-50%,-50%)",
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "rgba(34,197,94,0.12)",
              border: "1.5px solid rgba(34,197,94,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="10" r="4" stroke="#22c55e" strokeWidth="2" />
              <path
                d="M12 14 C12 14, 7 18, 7 21 C7 21, 12 23, 17 21 C17 21, 17 18, 12 14Z"
                stroke="#22c55e"
                strokeWidth="1.5"
                strokeLinejoin="round"
                fill="rgba(34,197,94,0.15)"
              />
            </svg>
          </Box>

          {[0, 120, 240].map((deg, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: i === 0 ? "#22c55e" : i === 1 ? "#3b82f6" : "#64748b",
                boxShadow: i === 0 ? "0 0 8px #22c55e" : "none",
                transform: `rotate(${deg}deg) translateX(52px) translateY(-50%)`,
                animation: `dotBlink 1.8s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
        </Box>

        <Box sx={{ textAlign: "center", animation: "fadeUp 0.6s 0.1s ease both", opacity: 0 }}>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 800,
              color: "#f8fafc",
              letterSpacing: -0.5,
              lineHeight: 1.1,
            }}
          >
            {t("Mapa.loadingTitle")}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#334155",
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: "uppercase",
              mt: 0.5,
            }}
          >
            {t("Mapa.loadingPozos")}
          </Typography>

          <Stack direction="row" justifyContent="center" spacing={0.6} mt={1.5}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: "#22c55e",
                  animation: "dotBlink 1.2s ease-in-out infinite",
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            animation: "fadeUp 0.6s 0.2s ease both",
            opacity: 0,
          }}
        >
          {["TS-03", "TS-03i", "TS-03B", "TS-03C"].map((name, i) => (
            <Box
              key={name}
              sx={{
                width: 120,
                borderRadius: "14px",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                opacity: 1 - i * 0.15,
              }}
            >
              <Box
                className="shimmer-box"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                  animationDelay: `${i * 0.15}s`,
                }}
              />
              <Box
                className="shimmer-box"
                sx={{
                  width: "60%",
                  height: 11,
                  borderRadius: 1,
                  animationDelay: `${i * 0.15 + 0.1}s`,
                }}
              />
              <Box
                className="shimmer-box"
                sx={{
                  width: "90%",
                  height: 8,
                  borderRadius: 1,
                  animationDelay: `${i * 0.15 + 0.2}s`,
                }}
              />
              <Box
                className="shimmer-box"
                sx={{
                  width: "75%",
                  height: 8,
                  borderRadius: 1,
                  animationDelay: `${i * 0.15 + 0.3}s`,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.3,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background:
                      i === 0 ? "#22c55e" : i === 1 ? "#3b82f6" : "#475569",
                    animation: i < 2 ? "dotBlink 1.4s ease-in-out infinite" : "none",
                    animationDelay: `${i * 0.3}s`,
                  }}
                />
                <Box
                  className="shimmer-box"
                  sx={{
                    width: "55%",
                    height: 7,
                    borderRadius: 1,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            width: 260,
            animation: "fadeUp 0.6s 0.3s ease both",
            opacity: 0,
          }}
        >
          <Stack direction="row" justifyContent="space-between" mb={0.6}>
            <Typography sx={{ fontSize: "10px", color: "#334155", fontWeight: 700, letterSpacing: 0.5 }}>
              {t("Mapa.textInicialized")}
            </Typography>
            <Typography sx={{ fontSize: "10px", color: "#22c55e", fontWeight: 700, fontFamily: "monospace" }}>
              {progress}%
            </Typography>
          </Stack>
          <Box
            sx={{
              height: 3,
              borderRadius: 99,
              background: "rgba(255,255,255,0.05)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${progress}%`,
                borderRadius: 99,
                background: "linear-gradient(90deg, #16a34a, #22c55e)",
                boxShadow: "0 0 10px rgba(34,197,94,0.5)",
                transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
              }}
            />
          </Box>

          <Stack direction="row" spacing={0.8} mt={1.5} justifyContent="center">
            {[
              { label: t("Mapa.pozos"), done: progress >= 60 },
              { label: t("Mapa.datos"), done: progress >= 90 },
            ].map(({ label, done }) => (
              <Box
                key={label}
                sx={{
                  px: 1,
                  py: 0.3,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: done ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.06)",
                  background: done ? "rgba(34,197,94,0.07)" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  transition: "all 0.4s ease",
                }}
              >
                {done ? (
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 3" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      border: "1px solid rgba(255,255,255,0.15)",
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 700,
                    color: done ? "#4ade80" : "#334155",
                    transition: "color 0.4s ease",
                    letterSpacing: 0.3,
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

const WELL_STATUS = {
  "TS-03": "done",
  "TS-03i": "done",
  "TS-03B": "done",
  "TS-03C": "done",
  "TS-03D": "done",
  "TS-04": "done",
  "TS-04a": "active",
};

const POZOS = [
  {
    id: "TS-03",
    nombre: "TS-03",
    proyecto: "Proyecto TacaSal IV",
    lat: -24.614407,
    lng: -67.799721,
    profundidad: 418.01,
    diasOperados: 47,
    rendimiento: 1.3,
    disabled: false,
    geologos: [
      { nombre: "Eduardo Pujol", color: "#6b7280" },
      { nombre: "Agustín Kanbour", color: "#6b7280" },
    ],
  },
  {
    id: "TS-03i",
    nombre: "TS-03i",
    proyecto: "Proyecto TacaSal IV",
    lat: -24.607698,
    lng: -67.784615,
    profundidad: 268,
    diasOperados: 34,
    rendimiento: null,
    disabled: false,
    geologos: [{ nombre: "Por asignar", color: "#f59e0b" }],
  },
  {
    id: "TS-03C",
    nombre: "TS-03C",
    proyecto: "Proyecto TacaSal IV",
    lat: -24.620794,
    lng: -67.775,
    profundidad: null,
    diasOperados: null,
    rendimiento: null,
    disabled: false,
    geologos: [{ nombre: "Por asignar", color: "#f59e0b" }],
  },
  {
    id: "TS-03B",
    nombre: "TS-03B",
    proyecto: "Proyecto TacaSal IV",
    lat: -24.627030,
    lng: -67.810020,
    profundidad: null,
    diasOperados: null,
    rendimiento: null,
    disabled: false,
    geologos: [{ nombre: "Por asignar", color: "#f59e0b" }],
  },
  {
    id: "TS-03D",
    nombre: "TS-03D",
    proyecto: "Proyecto TacaSal IV",
    lat: -24.620638,
    lng: -67.758522,
    profundidad: null,
    diasOperados: null,
    rendimiento: null,
    disabled: false,
    geologos: [{ nombre: "Por asignar", color: "#f59e0b" }],
  },
  {
    id: "TS-04",
    nombre: "TS-04",
    proyecto: "Proyecto TacaSal IV",
    lat: -24.631658,
    lng: -67.765722,
    profundidad: null,
    diasOperados: null,
    rendimiento: null,
    disabled: false,
    geologos: [{ nombre: "Por asignar", color: "#f59e0b" }],
  },
  {
    id: "TS-04a",
    nombre: "TS-04a",
    proyecto: "Proyecto TacaSal IV",
    lat: -24.629658,
    lng: -67.759722,
    profundidad: null,
    diasOperados: null,
    rendimiento: null,
    disabled: false,
    geologos: [{ nombre: "Por asignar", color: "#f59e0b" }],
  }
];

/* 
Más a la izquierda (oeste): disminuye lng.
Más a la derecha (este): aumenta lng.
Hacia arriba (norte): aumenta lat.
Hacia abajo (sur): disminuye lat.
*/

const MAP_BOUNDS = L.latLngBounds([-24.5, -67.6], [-24.8, -68.05]);
const MIN_ZOOM = 10;
const MAX_ZOOM = 16;

const ICON_BASE = 42;
const NORMAL_ZOOM = 12;
const ICON_MIN_SCALE = 0.5;

const zoomToIconScale = (zoom) => {
  if (zoom >= NORMAL_ZOOM) return 1;
  const t = Math.max(0, Math.min(1, (zoom - MIN_ZOOM) / (NORMAL_ZOOM - MIN_ZOOM)));
  return ICON_MIN_SCALE + t * (1 - ICON_MIN_SCALE);
};

const COMPACT_SCALE_THRESHOLD = 0.85;

const CLUSTER_PIXEL_THRESHOLD = 70;

const createDoneIcon = (scale = 1, nombre = "", compact = false) => {
  const size = ICON_BASE;
  const inner = compact
    ? `<span style="font-size:12px;font-weight:800;color:#fff;line-height:1;text-align:center;padding:0 2px;">${nombre}</span>`
    : `<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
         <path d="M5 13l4 4L19 7" stroke="white" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
       </svg>`;
  return L.divIcon({
    className: "",
    html: `
      <div style="width:${size}px;height:${size}px;transform:scale(${scale});transform-origin:center;transition:transform 0.15s ease-out;">
        <div style="position:relative;width:100%;height:100%;pointer-events:none;">
          <div style="position:absolute;inset:0;border-radius:50%;background:rgba(34,197,94,0.35);animation:pulseRing 2s ease-out infinite;animation-delay:0s;"></div>
          <div style="position:absolute;inset:0;border-radius:50%;background:rgba(34,197,94,0.35);animation:pulseRing 2s ease-out infinite;animation-delay:0.65s;"></div>
          <div style="position:absolute;inset:0;border-radius:50%;background:rgba(34,197,94,0.35);animation:pulseRing 2s ease-out infinite;animation-delay:1.3s;"></div>
          <div style="position:absolute;inset:0;border-radius:50%;background:linear-gradient(135deg,#16a34a,#22c55e);box-shadow:0 0 0 3px rgba(34,197,94,0.3),0 4px 18px rgba(22,163,74,0.4);display:flex;align-items:center;justify-content:center;border:3px solid #fff;overflow:hidden;">
            ${inner}
          </div>
        </div>
      </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const createIncomingIcon = (scale = 1, nombre = "", compact = false) => {
  const size = ICON_BASE;
  const inner = compact
    ? `<span style="font-size:12px;font-weight:800;color:#fff;line-height:1;text-align:center;padding:0 2px;">${nombre}</span>`
    : `<svg width="22" height="22" viewBox="0 0 24 24" fill="none">
         <circle cx="12" cy="12" r="3" fill="white"/>
         <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" stroke="white" stroke-width="2" stroke-linecap="round"/>
       </svg>`;
  return L.divIcon({
    className: "",
    html: `
      <div style="width:${size}px;height:${size}px;transform:scale(${scale});transform-origin:center;transition:transform 0.15s ease-out;">
        <div style="position:relative;width:100%;height:100%;pointer-events:none;">
          <div style="position:absolute;inset:0;border-radius:50%;background:rgba(59,130,246,0.35);animation:pulseRing 2s ease-out infinite;animation-delay:0s;"></div>
          <div style="position:absolute;inset:0;border-radius:50%;background:rgba(59,130,246,0.35);animation:pulseRing 2s ease-out infinite;animation-delay:0.65s;"></div>
          <div style="position:absolute;inset:0;border-radius:50%;background:rgba(59,130,246,0.35);animation:pulseRing 2s ease-out infinite;animation-delay:1.3s;"></div>
          <div style="position:absolute;inset:0;border-radius:50%;background:#3b82f6;display:flex;align-items:center;justify-content:center;border:3px solid #fff;overflow:hidden;">
            ${inner}
          </div>
        </div>
      </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const createDisabledIcon = (scale = 1, nombre = "", compact = false) => {
  const size = ICON_BASE;
  const inner = compact
    ? `<span style="font-size:11px;font-weight:800;color:#fff;line-height:1;text-align:center;padding:0 2px;">${nombre}</span>`
    : `<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
         <circle cx="12" cy="12" r="3" fill="rgba(255,255,255,0.5)"/>
         <path d="M12 5v7" stroke="rgba(255,255,255,0.5)" stroke-width="2.5" stroke-linecap="round"/>
       </svg>`;
  return L.divIcon({
    className: "",
    html: `
      <div style="width:${size}px;height:${size}px;transform:scale(${scale});transform-origin:center;transition:transform 0.15s ease-out;">
        <div style="position:relative;width:100%;height:100%;pointer-events:none;opacity:0.45;filter:grayscale(0.6);">
          <div style="position:absolute;inset:0;border-radius:50%;background:#64748b;display:flex;align-items:center;justify-content:center;border:3px solid rgba(255,255,255,0.3);box-shadow:0 2px 8px rgba(0,0,0,0.3);overflow:hidden;">
            ${inner}
          </div>
        </div>
      </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const createClusterIcon = (scale = 1, count = 2) => {
  const size = ICON_BASE + 10;
  return L.divIcon({
    className: "",
    html: `
      <div style="width:${size}px;height:${size}px;transform:scale(${scale});transform-origin:center;transition:transform 0.15s ease-out;">
        <div style="position:relative;width:100%;height:100%;pointer-events:none;">
          <div style="position:absolute;inset:0;border-radius:50%;background:rgba(59,130,246,0.3);animation:pulseRing 2.4s ease-out infinite;"></div>
          <div style="position:absolute;inset:0;border-radius:50%;background:linear-gradient(135deg,#1e3a8a,#3b82f6);box-shadow:0 0 0 3px rgba(59,130,246,0.28),0 4px 18px rgba(30,58,138,0.5);display:flex;align-items:center;justify-content:center;border:3px solid #fff;">
            <span style="font-size:15px;font-weight:800;color:#fff;">${count}</span>
          </div>
        </div>
      </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const CARD_W = 300;
const CARD_H_EST = 395;
const CARD_OFF_X = 255;
const CARD_OFF_Y = -310;

const MetricBox = ({ icon, label, value, unit }) => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.4,
      py: 1.4,
      px: 0.5,
      borderRadius: 2,
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.06)",
    }}
  >
    <Box sx={{ color: "#334155", "& svg": { fontSize: 15 } }}>{icon}</Box>
    <Typography sx={{ fontSize: "8px", fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1, textAlign: "center" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#f8fafc", fontFamily: "monospace", lineHeight: 1 }}>
      {value ?? "—"}
    </Typography>
    <Typography sx={{ fontSize: "9px", color: "#1e3a5f", fontWeight: 600 }}>
      {unit}
    </Typography>
  </Box>
);

const FloatingCardAnchor = ({ pozo, onClose, onNavigate, t, show }) => {
  const map = useMap();
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!show || !pozo) return;
    const update = () => {
      const p = map.latLngToContainerPoint([pozo.lat, pozo.lng]);
      setPos({ x: p.x, y: p.y });
    };
    update();
    map.on("move zoom viewreset", update);
    return () => map.off("move zoom viewreset", update);
  }, [map, pozo, show]);

  if (!show || !pozo) return null;

  const cardLeft = pos.x + CARD_OFF_X;
  const cardTop = pos.y + CARD_OFF_Y;

  const sx = pos.x + 7;
  const sy = pos.y - 22;
  const ex = cardLeft + 20;
  const ey = cardTop + CARD_H_EST - 14;
  const cp1x = pos.x + 60;
  const cp1y = pos.y - 160;
  const cp2x = cardLeft - 15;
  const cp2y = cardTop + CARD_H_EST + 12;

  const pathD = `M ${sx} ${sy} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${ex} ${ey}`;

  return (
    <>
      <Card
        sx={{
          position: "absolute",
          left: cardLeft,
          top: cardTop,
          width: CARD_W,
          zIndex: 1000,
          background: "rgba(7,11,24,0.96)",
          backdropFilter: "blur(28px)",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: "20px",
          boxShadow: "0 28px 70px rgba(0,0,0,0.75), 0 0 0 1px rgba(34,197,94,0.09)",
          overflow: "hidden",
          animation: "cardReveal 0.3s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <Box sx={{ height: "3px", background: "linear-gradient(90deg, #16a34a 0%, #22c55e 55%, transparent 100%)" }} />

        <Box sx={{ px: 2, pt: 1.8, pb: 1.4 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Stack spacing={0.7} sx={{ flex: 1, minWidth: 0 }}>
              <Chip
                size="small"
                icon={<FiberManualRecordIcon sx={{ fontSize: "7px !important", color: "#16a34a !important" }} />}
                label={t("Mapa.pozo_finalizado") || "Finalizado"}
                sx={{
                  alignSelf: "flex-start",
                  height: 20,
                  fontSize: "9px",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  background: "rgba(22,163,74,0.1)",
                  border: "1px solid rgba(22,163,74,0.28)",
                  color: "#4ade80",
                  "& .MuiChip-icon": { ml: "6px" },
                  "& .MuiChip-label": { px: "8px" },
                }}
              />
              <Typography sx={{ fontSize: "20px", fontWeight: 800, color: "#f8fafc", lineHeight: 1.1, letterSpacing: -0.4 }}>
                {pozo.nombre}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#475569", fontWeight: 500 }}>
                {pozo.proyecto}
              </Typography>
            </Stack>

            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                mt: 0.3, width: 28, height: 28, flexShrink: 0,
                background: "rgba(255,255,255,0.05)",
                color: "#475569",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "8px",
                "&:hover": { background: "rgba(255,255,255,0.12)", color: "#f8fafc" },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ px: 2, pt: 1.4, pb: 2 }}>
          <Button
            fullWidth variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={onNavigate}
            sx={{
              background: "linear-gradient(135deg,#1565C0 0%,#2979FF 100%)",
              color: "#fff", fontWeight: 700, fontSize: "13px",
              borderRadius: "12px", py: 1.1, textTransform: "none", letterSpacing: 0.2,
              boxShadow: "0 4px 18px rgba(41,121,255,0.26)",
              "&:hover": { background: "linear-gradient(135deg,#1976D2 0%,#448AFF 100%)", boxShadow: "0 6px 26px rgba(41,121,255,0.42)", transform: "translateY(-1px)" },
              transition: "all 0.18s ease",
            }}
          >
            {t("Mapa.navigation")}
          </Button>
        </Box>
      </Card>
    </>
  );
};

const ClickablePulseCircles = ({ pozo, status, navigate, scale = 1 }) => {
  const [tick, setTick] = useState(0);
  const map = useMap();
  const baseColor = status === "done" ? "#22c55e" : "#fb923c";
  const rgb = status === "done" ? "34,197,94" : "251,146,60";

  const iconRadiusMeters = () => {
    const halfIcon = (ICON_BASE / 2) * scale;
    const center = map.latLngToContainerPoint([pozo.lat, pozo.lng]);
    const edge = map.containerPointToLatLng(L.point(center.x + halfIcon, center.y));
    return map.distance([pozo.lat, pozo.lng], edge);
  };

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 150);
    return () => clearInterval(id);
  }, []);

  const CYCLE = 50, OFFSETS = [0, 20, 40];
  const startR = iconRadiusMeters();

  //esto es el efecto naranja que se ve en los pozos activos, que hace que el circulo crezca y desaparezca
  const maxR = startR + 500;

  const handleClick = () => {
    if (!pozo.disabled) navigate(`/panel/diario/${pozo.id}`);
  };

  if (pozo.disabled) return null;

  return (
    <>
      {OFFSETS.map((offset, i) => {
        const progress = ((tick + offset) % CYCLE) / CYCLE;
        const radius = startR + progress * (maxR - startR);
        const opacity = Math.max(0, (1 - progress) * 0.28);
        return (
          <Circle
            key={i}
            center={[pozo.lat, pozo.lng]}
            radius={radius}
            className="clickable-pulse"
            pathOptions={{
              color: `rgba(${rgb},${opacity * 1.8})`,
              fillColor: baseColor,
              fillOpacity: opacity * 0.35,
              stroke: true,
              weight: 1.5,
              interactive: true,
            }}
            eventHandlers={{ click: handleClick }}
          />
        );
      })}
    </>
  );
};

const WellLabels = ({ pozos, getIcon, scale = 1 }) => (
  <>
    {pozos.map((pozo) => (
      <Marker
        key={pozo.id}
        position={[pozo.lat, pozo.lng]}
        icon={getIcon(pozo, scale)}
        interactive={false}
        pane="markers-back"
      >
        <Tooltip
          permanent
          direction="top"
          offset={[0, -(ICON_BASE / 2 + 8) * scale]}
          className="well-tooltip"
        >
          {pozo.nombre}
        </Tooltip>
      </Marker>
    ))}
  </>
);

const ClusterMarker = ({ group, lat, lng, scale = 1 }) => {
  const map = useMap();
  const icon = createClusterIcon(scale, group.length);
  const size = ICON_BASE + 10;

  const handleClick = () => {
    const bounds = L.latLngBounds(group.map((p) => [p.lat, p.lng]));
    map.flyToBounds(bounds.pad(0.8), {
      maxZoom: MAX_ZOOM,
      duration: 0.45,
      easeLinearity: 0.35,
    });
  };

  return (
    <Marker
      position={[lat, lng]}
      icon={icon}
      eventHandlers={{ click: handleClick }}
    >
      <Tooltip
        permanent
        direction="top"
        offset={[0, -(size / 2 + 8) * scale]}
        className="cluster-tooltip"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {group.map((p) => (
            <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <Box
                component="span"
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: WELL_STATUS[p.id] === "done" ? "#22c55e" : "#3b82f6",
                  flexShrink: 0,
                }}
              />
              <span>{p.nombre}</span>
            </Box>
          ))}
        </Box>
      </Tooltip>
    </Marker>
  );
};

const ClusterEngine = ({ pozos, threshold, onClusters }) => {
  const map = useMap();

  useEffect(() => {
    const recompute = () => {
      const zoom = map.getZoom();
      const points = pozos.map((p) => ({
        pozo: p,
        pt: map.project([p.lat, p.lng], zoom),
      }));

      const used = new Array(points.length).fill(false);
      const groups = [];

      for (let i = 0; i < points.length; i++) {
        if (used[i]) continue;
        const group = [points[i].pozo];
        used[i] = true;
        for (let j = i + 1; j < points.length; j++) {
          if (used[j]) continue;
          if (points[i].pt.distanceTo(points[j].pt) < threshold) {
            group.push(points[j].pozo);
            used[j] = true;
          }
        }
        groups.push(group);
      }

      onClusters(groups);
    };

    recompute();
    map.on("zoomend moveend", recompute);
    return () => map.off("zoomend moveend", recompute);
  }, [map, pozos, threshold, onClusters]);

  return null;
};

const MapCenterer = ({ well, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (well) map.setView([well.lat, well.lng], zoom);
  }, [map]);
  return null;
};

const ZoomWatcher = ({ onZoomChange }) => {
  const map = useMap();
  useEffect(() => {
    const update = () => onZoomChange(map.getZoom());
    update();
    map.on("zoomend", update);
    return () => map.off("zoomend", update);
  }, [map, onZoomChange]);
  return null;
};

const ClickToGetCoords = () => {
  const map = useMap();
  useEffect(() => {
    const onClick = (e) => {
    };
    map.on("click", onClick);
    return () => map.off("click", onClick);
  }, [map]);
  return null;
};

const ZoomHintDismissListener = ({ onInteract }) => {
  const map = useMap();
  useEffect(() => {
    const handle = () => onInteract();
    map.on("zoomstart dragstart", handle);
    return () => map.off("zoomstart dragstart", handle);
  }, [map, onInteract]);
  return null;
};

const ZoomHint = ({ visible, t }) => {
  return (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        bottom: 36,
        transform: `translateX(-50%) translateY(${visible ? "0" : "14px"})`,
        zIndex: 1200,
        opacity: visible ? 1 : 0,
        pointerEvents: "none",
        transition: "opacity 0.6s cubic-bezier(0.4,0,0.2,1), transform 0.6s cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      <style>{`
        @keyframes zoomHintScroll {
          0%   { transform: translateY(0);    opacity: 0.9; }
          50%  { transform: translateY(5px);  opacity: 0.35; }
          100% { transform: translateY(0);    opacity: 0.9; }
        }
        @keyframes zoomHintGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.35), 0 14px 34px rgba(0,0,0,0.55); }
          50%      { box-shadow: 0 0 0 7px rgba(34,197,94,0), 0 14px 34px rgba(0,0,0,0.55); }
        }
        @keyframes zoomHintMagnify {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50%      { transform: scale(1.12) rotate(-6deg); }
        }
      `}</style>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          pl: 1.4,
          pr: 2,
          py: 1.1,
          borderRadius: "999px",
          background: "rgba(7,11,24,0.86)",
          backdropFilter: "blur(18px)",
          border: "1px solid rgba(34,197,94,0.25)",
          animation: "zoomHintGlow 2.2s ease-in-out infinite",
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#16a34a,#22c55e)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "zoomHintMagnify 1.8s ease-in-out infinite",
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="6" stroke="white" strokeWidth="2.2" />
            <path d="M20 20l-3.2-3.2" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M11 8.3v5.4M8.3 11h5.4" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </Box>

        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: "#f8fafc",
            letterSpacing: 0.1,
            whiteSpace: "nowrap",
          }}
        >
          {t("Mapa.zoomHint")}
        </Typography>

        <Box
          sx={{
            width: 16,
            height: 24,
            borderRadius: "10px",
            border: "1.6px solid rgba(255,255,255,0.35)",
            display: "flex",
            justifyContent: "center",
            pt: "4px",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 3,
              height: 6,
              borderRadius: "2px",
              background: "#22c55e",
              animation: "zoomHintScroll 1.4s ease-in-out infinite",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const TutorialOverlay = ({ visible, onDismiss, t }) => {
  if (!visible) return null;

  const primary = "#0B1132";

  return (
    <>
      <style>{`
        @keyframes tutorialGlow {
          0%, 100% { 
            box-shadow: 0 0 0 4px ${primary}80, 0 0 20px ${primary}99, 0 0 40px ${primary}33; 
          }
          50% { 
            box-shadow: 0 0 0 10px ${primary}26, 0 0 32px ${primary}d9, 0 0 60px ${primary}59; 
          }
        }
        @keyframes tutorialGlowAttribution {
          0%, 100% { 
            box-shadow: 0 0 0 3px rgba(59,130,246,0.45), 0 0 14px rgba(59,130,246,0.45), 0 0 28px rgba(59,130,246,0.15); 
          }
          50% { 
            box-shadow: 0 0 0 8px rgba(59,130,246,0.12), 0 0 24px rgba(59,130,246,0.7), 0 0 44px rgba(59,130,246,0.25); 
          }
        }

        .leaflet-control-zoom {
          z-index: 1300 !important;        
          isolation: isolate !important;   
          opacity: 1 !important;
          background: white !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
          animation: none !important;
          border-radius: 8px !important;
          overflow: hidden !important;
        }
        .leaflet-control-zoom a {
          background: white !important;
          color: #333 !important;
          opacity: 1 !important;
          border-radius: 0 !important;
          transition: background 0.2s ease !important;
        }
        .leaflet-control-zoom a:hover {
          background: #f0f0f0 !important;
        }

        .leaflet-control-attribution {
          z-index: 1300 !important;
          position: relative !important;
          border-radius: 6px !important;
          animation: tutorialGlowAttribution 2.2s ease-in-out infinite !important;
          background: rgba(7,11,24,0.92) !important;
          color: #93c5fd !important;
          padding: 4px 10px !important;
          font-weight: 600 !important;
        }
      `}</style>

      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          background: "rgba(0,0,0,0.65)",
          backdropFilter: "blur(1.5px)",
          pointerEvents: "auto",
          touchAction: "none",
          WebkitTouchCallout: "none",
        }}
      />

      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 2000,
          pointerEvents: "all",
        }}
      >
        <Card
          sx={{
            width: 380,
            maxWidth: "92vw",
            background: "rgba(7,11,24,0.97)",
            backdropFilter: "blur(30px)",
            border: `1px solid ${primary}33`,
            borderRadius: "22px",
            boxShadow: `0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px ${primary}14`,
            overflow: "hidden",
          }}
        >
          <Box sx={{ height: "3px", background: primary }} />

          <Box sx={{ p: 3, textAlign: "center" }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
                boxShadow: `0 8px 28px ${primary}66`,
              }}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="6" stroke="white" strokeWidth="2.2" />
                <path d="M20 20l-4-4" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
                <path d="M11 8v6M8 11h6" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </Box>

            <Typography
              sx={{
                fontSize: "19px",
                fontWeight: 800,
                color: "#f8fafc",
                letterSpacing: -0.4,
                mb: 1,
              }}
            >
              {t("Mapa.tutorialTitle") || "¡Explorá el mapa!"}
            </Typography>

            <Typography
              sx={{
                fontSize: "13px",
                color: "#94a3b8",
                lineHeight: 1.6,
                mb: 2.5,
                px: 1,
              }}
            >
              {t("Mapa.tutorialBody") ||
                "Usá los botones + y − para acercar o alejar el mapa. También podés usar el scroll del mouse o pellizcar en dispositivos táctiles para ver tus pozos en detalle."}
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ mb: 2.5 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  px: 1.5,
                  py: 0.7,
                  borderRadius: "8px",
                  background: `${primary}14`,
                  border: `1px solid ${primary}33`,
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "4px",
                    background: primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "14px",
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  +
                </Box>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "4px",
                    background: primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "14px",
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  −
                </Box>
                <Typography sx={{ fontSize: "10px", color: "#93c5fd", fontWeight: 700 }}>
                  Zoom
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  px: 1.5,
                  py: 0.7,
                  borderRadius: "8px",
                  background: "rgba(59,130,246,0.08)",
                  border: "1px solid rgba(59,130,246,0.2)",
                }}
              >
                <Typography sx={{ fontSize: "10px", color: "#60a5fa", fontWeight: 700 }}>
                  <Info />
                </Typography>
                <Typography sx={{ fontSize: "10px", color: "#60a5fa", fontWeight: 700 }}>
                  {t("Mapa.tutorialAttribution") || "Información"}
                </Typography>
              </Box>
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={onDismiss}
              sx={{
                background: primary,
                color: "#fff",
                fontWeight: 700,
                fontSize: "14px",
                borderRadius: "12px",
                py: 1.2,
                textTransform: "none",
                letterSpacing: 0.3,
                boxShadow: `0 4px 18px ${primary}59`,
                "&:hover": {
                  background: "#162044",
                  boxShadow: `0 6px 26px ${primary}80`,
                  transform: "translateY(-1px)",
                },
                transition: "all 0.18s ease",
              }}
            >
              {t("Mapa.tutorialOk") || "¡Entendido!"}
            </Button>
          </Box>
        </Card>
      </Box>
    </>
  );
};

const MapaProyectos = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showDoneCard, setShowDoneCard] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [clusters, setClusters] = useState(() => [POZOS]);
  const [showZoomHint, setShowZoomHint] = useState(false);
  const [hintShown, setHintShown] = useState(false);
  const mapRef = useRef();

  const [showTutorial, setShowTutorial] = useState(() => {
    try {
      return localStorage.getItem("mapa_tutorial_done") !== "true";
    } catch {
      return true;
    }
  });

  const handleDismissTutorial = () => {
    try {
      localStorage.setItem("mapa_tutorial_done", "true");
    } catch {
    }
    setShowTutorial(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => setMapReady(true), 2200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!showTutorial && !hintShown && mapReady) {
      const timer = setTimeout(() => {
        setShowZoomHint(true);
        setHintShown(true);
        setTimeout(() => setShowZoomHint(false), 5000);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [showTutorial, hintShown, mapReady]);

  const doneWell = POZOS.find((p) => p.nombre === "TS-03i");
  const [zoom, setZoom] = useState(10);
  const iconScale = zoomToIconScale(zoom);

  const getIcon = (pozo, scale) => {
    if (pozo.disabled) return createDisabledIcon(scale);
    return WELL_STATUS[pozo.id] === "done" ? createDoneIcon(scale) : createIncomingIcon(scale);
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: "100vh", overflow: "hidden" }}>
      <style>{`
        .clickable-pulse { cursor: pointer !important; pointer-events: auto !important; }
        .leaflet-interactive { cursor: pointer !important; }

        @keyframes cardReveal {
          from { opacity: 0; transform: translateY(-12px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)      scale(1); }
        }
        @keyframes dashFlow {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: -22; }
        }
        @keyframes pulseRing {
          0%   { transform: scale(1); opacity: 0.55; }
          100% { transform: scale(7); opacity: 0; }
        }

        .well-tooltip {
          background: #ffffff !important;
          border: none !important;
          border-radius: 20px !important;
          padding: 6px 14px !important;
          font-family: 'Segoe UI', sans-serif !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          color: #1e1e1e !important;
          box-shadow: 0 2px 6px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15) !important;
          pointer-events: none !important;
          white-space: nowrap;
        }
        .well-tooltip::before {
          border-top-color: #ffffff !important;
        }

        .cluster-tooltip {
          background: #0f172a !important;
          border: none !important;
          border-radius: 14px !important;
          padding: 8px 12px !important;
          font-family: 'Segoe UI', sans-serif !important;
          font-weight: 600 !important;
          font-size: 12px !important;
          color: #f8fafc !important;
          box-shadow: 0 6px 20px rgba(0,0,0,0.45) !important;
          pointer-events: none !important;
          white-space: nowrap;
        }
        .cluster-tooltip::before {
          border-top-color: #0f172a !important;
        }

        .leaflet-tooltip {
          transition: opacity 0.15s linear;
        }
.leaflet-control-zoom {
  opacity: 1 !important;
  background: white !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
  z-index: 1500 !important;
  isolation: isolate !important;
  filter: none !important;
  backdrop-filter: none !important;
}
.leaflet-control-zoom a {
  background: white !important;
  color: #333 !important;
  opacity: 1 !important;
}
      `}</style>

      {/* <LoadingScreen visible={!mapReady} t={t} /> */}

      <MapContainer
        ref={mapRef}
        center={[-24.645, -67.78]}
        zoom={10}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        maxBounds={MAP_BOUNDS}
        maxBoundsViscosity={1.0}
        dragging={true}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        touchZoom={true}
        boxZoom={false}
        keyboard={false}
        tap={true}
        zoomControl={true}
        style={{ width: "100%", height: "100%", minHeight: "100vh", background: "#0c1220" }}
      >
        <MapCenterer well={doneWell} zoom={12} />
        <ZoomWatcher onZoomChange={setZoom} />
        <ClusterEngine pozos={POZOS} threshold={CLUSTER_PIXEL_THRESHOLD} onClusters={setClusters} />
        <ZoomHintDismissListener onInteract={() => setShowZoomHint(false)} />
        {/* <ClickToGetCoords /> */}

        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxZoom={19} />
        <TileLayer url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" maxZoom={19} />

        <Polygon
          positions={[[-24.6, -67.82], [-24.6, -67.70], [-24.69, -67.70], [-24.69, -67.95], [-24.665, -67.95], [-24.665, -67.82]]}
          pathOptions={{ color: "#0f0f47", fillColor: "#0f0f47", fillOpacity: 0.2, weight: 2, dashArray: "0", interactive: false }}
        />

        <Pane name="markers-back" style={{ zIndex: 200 }} />

        {clusters.map((group) => {
          if (group.length === 1) {
            const pozo = group[0];
            return (
              <Fragment key={pozo.id}>
                <ClickablePulseCircles pozo={pozo} status={WELL_STATUS[pozo.id]} navigate={navigate} scale={iconScale} />
                <WellLabels pozos={[pozo]} getIcon={getIcon} scale={iconScale} />
              </Fragment>
            );
          }

          const centerLat = group.reduce((s, p) => s + p.lat, 0) / group.length;
          const centerLng = group.reduce((s, p) => s + p.lng, 0) / group.length;
          const key = group.map((p) => p.id).sort().join("-");

          return (
            <ClusterMarker key={key} group={group} lat={centerLat} lng={centerLng} scale={iconScale} />
          );
        })}
      </MapContainer>

      {!showTutorial && <ZoomHint visible={showZoomHint} t={t} />}

      <TutorialOverlay
        visible={showTutorial}
        onDismiss={handleDismissTutorial}
        t={t}
      />
    </Box>
  );
};

export default MapaProyectos;